export const REFERRAL_CODE_STORAGE_KEY = "homehelpy_referral_code";

/**
 * A stored code older than this is ignored. Without it, a link clicked months ago
 * silently attributes an unrelated later signup on the same device — the code is only
 * ever cleared by a successful signup, so it otherwise lives forever.
 */
const REFERRAL_CODE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * In-memory mirror of the stored code.
 *
 * localStorage is best-effort: Safari private browsing throws on setItem, ITP can clear
 * it between page loads, and an in-app webview (WhatsApp, Instagram) may drop it across
 * the OAuth hop. Every one of those failures is swallowed silently, so the signup POST
 * went out with no referralCode at all and the referral was lost with no error and no
 * log — the backend then took the `missing_code` branch, which logs nothing either.
 *
 * The mirror guarantees that a code validated in this page session still reaches the API
 * even when storage is entirely unavailable. It does NOT replace localStorage, which is
 * what carries the code across the /ref/:code -> /signup navigation and across a mobile
 * OAuth redirect.
 */
let inMemoryReferralCode: string | null = null;

/**
 * Set once an account is created with a referral code attached, and cleared by the
 * first successful login. Signup consumes the referral code immediately (it is sent
 * to the backend and removed from storage), so this separate flag is what survives
 * the email-verification gap between "registered with a code" and "logged in".
 */
export const REFERRAL_SIGNUP_REDIRECT_KEY = "homehelpy_referral_signup_redirect";
export const REFERRAL_REWARD_REMINDER_KEY = "homehelpy_referral_reward_reminder";

export const MY_REWARDS_PATH = "/my-rewards";

function reminderKeyForUser(userId: string | number | null | undefined) {
  const normalized = String(userId ?? "").trim();
  return normalized
    ? `${REFERRAL_REWARD_REMINDER_KEY}:${normalized}`
    : REFERRAL_REWARD_REMINDER_KEY;
}

export function storeReferralCode(code: string): void {
  const normalized = code.trim().toUpperCase();
  if (!normalized) return;

  // Mirror first — it has to hold even when the write below throws.
  inMemoryReferralCode = normalized;
  try {
    localStorage.setItem(
      REFERRAL_CODE_STORAGE_KEY,
      JSON.stringify({ code: normalized, ts: Date.now() }),
    );
  } catch {
    // localStorage unavailable (private browsing) — the mirror still carries the
    // code through this page session, which is all signup needs.
  }
}

export function getStoredReferralCode(): string | null {
  if (inMemoryReferralCode) return inMemoryReferralCode;

  let raw: string | null = null;
  try {
    raw = localStorage.getItem(REFERRAL_CODE_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  // Codes written before the TTL change are bare strings, not JSON. Honour them rather
  // than dropping every in-flight referral on the deploy boundary.
  let code = raw;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "code" in parsed) {
      const { code: storedCode, ts } = parsed as { code: unknown; ts?: unknown };
      if (typeof storedCode !== "string") return null;
      if (typeof ts === "number" && Date.now() - ts > REFERRAL_CODE_TTL_MS) {
        clearStoredReferralCode();
        return null;
      }
      code = storedCode;
    }
  } catch {
    // Not JSON — legacy bare string, use as-is.
  }

  inMemoryReferralCode = code;
  return code;
}

/** Clears the mirror and the stored copy. Call once the code has been sent to the API. */
export function clearStoredReferralCode(): void {
  inMemoryReferralCode = null;
  try {
    localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
  } catch {
    // Nothing stored to clear.
  }
}

/** Flags the account just created as referral-sourced, so its first login lands on My Rewards. */
export function markReferralSignup() {
  try {
    localStorage.setItem(REFERRAL_SIGNUP_REDIRECT_KEY, "1");
    localStorage.setItem(REFERRAL_REWARD_REMINDER_KEY, "1");
  } catch {
    // localStorage unavailable — user simply lands on the dashboard as before.
  }
}

/** Claims the signup reminder for the authenticated homeowner so it survives reloads safely. */
export function claimReferralRewardReminder(
  userId: string | number | null | undefined,
): boolean {
  try {
    const userKey = reminderKeyForUser(userId);
    const hasUserReminder = localStorage.getItem(userKey) === "1";
    const hasPendingSignupReminder =
      localStorage.getItem(REFERRAL_REWARD_REMINDER_KEY) === "1";

    if (hasPendingSignupReminder && userKey !== REFERRAL_REWARD_REMINDER_KEY) {
      localStorage.setItem(userKey, "1");
      localStorage.removeItem(REFERRAL_REWARD_REMINDER_KEY);
      return true;
    }

    return hasUserReminder || hasPendingSignupReminder;
  } catch {
    return false;
  }
}

export function clearReferralRewardReminder(
  userId: string | number | null | undefined,
) {
  try {
    localStorage.removeItem(REFERRAL_REWARD_REMINDER_KEY);
    localStorage.removeItem(reminderKeyForUser(userId));
  } catch {
    // localStorage unavailable - the in-memory reminder still clears.
  }
}

/** Reads and clears the flag. True only for the first login after a referral signup. */
export function consumeReferralSignupRedirect(): boolean {
  try {
    const flagged = localStorage.getItem(REFERRAL_SIGNUP_REDIRECT_KEY) === "1";
    if (flagged) localStorage.removeItem(REFERRAL_SIGNUP_REDIRECT_KEY);
    return flagged;
  } catch {
    return false;
  }
}

/**
 * Destination for a just-completed login.
 *
 * A referral signup lands on My Rewards so the new user sees the welcome voucher that
 * brought them in. The flag is consumed either way, so it never leaks into a later login.
 * `hasExplicitTarget` covers the flows that already have somewhere specific to go
 * (a saved booking, a `?redirect=` param, a protected route that bounced to login) —
 * those always win.
 */
export function resolvePostAuthRedirect(
  target: string,
  hasExplicitTarget = false,
): string {
  const referralSignup = consumeReferralSignupRedirect();
  return referralSignup && !hasExplicitTarget ? MY_REWARDS_PATH : target;
}
